'use strict';

import moment from 'moment';
import { initLottie } from '../../utils';

export class SchedulingCalendarWidget {
    dateFormat = "YYYY-MM-DD";

    /**
     * Initializes Scheduling Calendar Widget
     * 
     * @param {*} containerId - HTML container ID where the Scheduling Calendat Widget will be inserted
     * @param {*} changeDayCallback - this callback fn will be called when a user selects a date in the widget; returns date (YYYY-MM-DD)
     * @returns 
     */
    constructor(
        containerId,
        changeDayCallback = _ => { }
    ) {
        this.container = document.querySelector(`#${containerId}`);
        if (!this.container) {
            console.error('Unable to initiate SchedulingCalendarWidget');
            return;
        }
        this.changeDayCallback = changeDayCallback;
        this.maxMonths = 15;
        this.calendarHidden = true;
        this.date = moment();
        this.createWidgetHTML();
    }
    
    async loadAppointmentData(searchParams = null) {
        if (this.isLoading) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
        if (!searchParams) {
            searchParams = new URLSearchParams(window.location.search);
        }
        this.setLoading(true);
        try {
            const response = await fetch(`/schedule/get-calendar-data?${searchParams.toString()}`, {
                signal: this.abortController.signal,
            });
            const appointmentsData = await response.json();
            this.appointmentsData = appointmentsData;
            const firstDateWithAvailability = Object.keys(appointmentsData).find(
				(d) => appointmentsData[d] >= 1
			);
            this.firstDateWithAvailability = firstDateWithAvailability ?? moment().format('YYYY-MM-DD');
            const dates = Object.keys(appointmentsData).map(date => moment(date, this.dateFormat));
            const months = dates.map(date => date.format('M'));
            const uniqueMonths = [...new Set(months)];
            this.maxMonths = uniqueMonths.length;
            this.renderWidgetContainerHTML();
            this.setLoading(false);
        } catch (error) {
            return;
        }
    }

    setLoading(loading = true) {
        this.isLoading = loading;
        const loader = this.containerElement.querySelector('#calendarLoader');
        if (loading) {
            loader.classList.remove('hidden');
        } else {
            loader.classList.add('hidden');
        }
    }

    createWidgetHTML() {
        const fragment = document.createDocumentFragment();
        this.containerElement = document.createElement('div');
        this.renderWidgetContainerHTML();
        fragment.appendChild(this.containerElement);
        this.container.appendChild(fragment);
        initLottie();
    }

    renderWidgetContainerHTML() {
        this.containerElement.innerHTML = this.widgetContainerHTML();
        this.enableChevrons();
        this.openHideCalendarTrigger();
        if (this.appointmentsData) {
            this.markDatesWithData();
        } else {
            this.setupDayClickListeners();
        }
        initLottie();
    }

    setupDayClickListeners() {
        const today = moment();
        this.containerElement.querySelectorAll('.days .day').forEach((day) => {
            // mark previous dates in the current month as invalid to prevent click
            if (moment(day.dataset.date).isBefore(today, 'day')) {
                day.classList.add('invalid');
                return;
            }
            day.addEventListener('click', this.clickDay.bind(this));
        })
    }

    markDatesWithData() {
        let dates = Object.keys(this.appointmentsData);
        // normalize dates
        dates = dates.map(date => moment(date, this.dateFormat).format(this.dateFormat));
        this.containerElement.querySelectorAll('.days .day').forEach(day => {
            const appointmentsCount = this.appointmentsData[day.dataset.date] ?? 0;
            if (appointmentsCount >= 6) {
                day.classList.add('many');
                day.addEventListener('click', this.clickDay.bind(this));
            } else if (appointmentsCount > 0) {
                day.classList.add('few');
                day.addEventListener('click', this.clickDay.bind(this));
            } else {
                day.classList.add('invalid');
            }
        })
    }

    clickDay({ target }) {
        this.changeDayCallback(target.dataset.date);
        this.containerElement.querySelector('.widget-content .date').innerText = moment(target.dataset.date, this.dateFormat).format('MMMM D, YYYY');
        this.containerElement.querySelector('.widget-content').classList.toggle('hidden');
    }

    dayLabelsHTML = _ => `
        <div class="day-labels">
            <span>Su</span>
            <span>Mo</span>
            <span>Tu</span>
            <span>We</span>
            <span>Th</span>
            <span>Fr</span>
            <span>Sa</span>
        </div>
    `;

    monthDaysViewHTML = (month, year) => {
        const firstOfTheMonth = moment(`${year}-${month}-1`, this.dateFormat);

        // 0-6, Sunday - Saturday
        let weekDay = parseInt(firstOfTheMonth.format('d'));
        const daysInMonth = firstOfTheMonth.daysInMonth();
        const numberOfCellsToFill = weekDay + daysInMonth;

        return `
            <div class="days">
                ${Array(numberOfCellsToFill).fill(0).map((_, index) => {
            const day = index - weekDay + 1;
            // normalize the date
            const date = moment(`${year}-${month}-${day}`, this.dateFormat).format(this.dateFormat);
            return `
                    <span class="day" data-date="${day > 0 ? date : ''}">${index < weekDay ? '' : day}</span>
                    `;
        }).join('')}
            </div>
        `;
    };

    // sanitize the date
    dateHTML = date => `
        <span class="date">${moment(date, this.dateFormat).format('MMMM D, YYYY')}</span>
    `;

    monthViewHTML = (month, year) => `
        <div class="month">
            <span class="month-and-year noselect">${moment(`${year}-${month}`, 'YYYY-MM').format('MMMM YYYY')}</span>
            ${this.dayLabelsHTML()}
            ${this.monthDaysViewHTML(month, year)}
        </div>
    `;

    monthsHTML = () => {
        const nextMonth = moment(this.date).add(
			1,
			"month"
		);
        return `
            <span class="material-icons left noselect">chevron_left</span>
            <span class="material-icons right noselect">chevron_right</span>
            ${this.monthViewHTML(this.date.format('MM'), this.date.format('YYYY'))}
            ${this.monthViewHTML(nextMonth.format('MM'), nextMonth.format('YYYY'))}
        `
    }

    enableChevrons() {
        const arrowLeft = this.containerElement.querySelector('.material-icons.left');
        const arrowRight = this.containerElement.querySelector('.material-icons.right');
        const prevMonth = moment(this.date).subtract(1, 'month');
        const nextMonth = moment(this.date).add(1, 'month');

        if (this.date.isSameOrBefore(moment())) {
            arrowLeft.classList.add('disabled');
        } else {
            arrowLeft.classList.remove('disabled');
        }

        if (this.date.isSameOrAfter(moment().add(this.maxMonths - 2, 'months'))) {
            arrowRight.classList.add('disabled-mobile');
        } else if (this.date.isSameOrAfter(moment().add(this.maxMonths - 3, 'months'))) {
            arrowRight.classList.add('disabled-desktop');
        } else {
            arrowRight.classList.remove('disabled');
        }

        arrowLeft.addEventListener('click', () => {
            if (arrowLeft.classList.contains('disabled')) {
                return;
            }
            this.date = prevMonth;
            this.renderWidgetContainerHTML();
        })

        arrowRight.addEventListener('click', () => {
            if (arrowRight.classList.contains('disabled')) {
                return;
            }
            this.date = nextMonth;
            this.renderWidgetContainerHTML();
        })
    }

    widgetContainerHTML = () => {
        const date = this.date.format(this.dateFormat);
        return `
            <div id="scheduling-calendar-widget">
                <span class="calendar-icon"></span>
                <div class="widget-content ${this.calendarHidden ? 'hidden' : ''}">
                    <div id="calendarLoader" class="hidden">
                        <div class="lottie"></div>
                    </div>
                    ${this.dateHTML(date)}
                    <hr>
                    <div class="months">
                        ${this.monthsHTML()}
                    </div>
                    ${this.appointmentsData ? `<div class="legend">
                        <div>
                            <span class="preview dark"></span>&nbsp;More than 5 appointments
                        </div>
                        <div>
                            <span class="preview light"></span>&nbsp;5 or fewer appointments
                        </div>
                    </div>` : ``}
                </div>
            </div>
        `;
    }

    openHideCalendarTrigger() {
        this.containerElement.querySelector('.calendar-icon').addEventListener('click', e => {
			e.stopPropagation();
            this.containerElement.querySelector('.widget-content').classList.toggle('hidden');
            this.calendarHidden = !this.calendarHidden;
        });
    }

    toggleVisibility() {
        this.containerElement.querySelector('.widget-content').classList.toggle('hidden');
        this.calendarHidden = !this.calendarHidden;
    }
}